import { UserSession, UserSessionKeys } from 'types/User/firebaseTypes';
import { DataTableColumn } from './DataTable';

/** Session data related specifically to table settings used in ops portal. */
export interface OpsPortalColumnSettings {
    /** Map to Column Sorting Order in MUI */
    col_order?: string[];
    /** Map to Column Visibility Model in MUI */
    columns?: Record<string, boolean>;
}

/** Session data related specifically to DataTable settings mapped to MUI-specific configurations. */
export interface BackOfficeSession {
    sortingOrder: string[];
    visibilityModel: Record<string, boolean>;
}

/**
 * Extract + convert MUI Datagrid compatible data structure for sorting and column visibility from UserSession data.
 * @note in the future this function should be removed and one data structure should serve for backoffice.
 * We should abandon ops fields where necessary and build a data structure for the current MUI DataGrid implementation.
 * @param opsPortalSession - UserSession object to be converted to BackOffice Data Table compatible object
 * @param gridId - key of the grid session we are mapping. this value also corresponds to a particular session entry of UserSession data.
 * @param columns - columns from the data grid to assist with mapping. Acts as a lookup object
 * @returns data structure of sorting and column visibility configs compatible with MUI's DataGrid
 */
export const mapUserSessionToMuiDataGrid = (
    opsPortalSession: UserSession,
    gridId: UserSessionKeys,
    columns: DataTableColumn[]
) => {
    const currentGridSession: any = opsPortalSession[gridId];

    let visibilityModel = undefined;
    let sortingOrder = undefined;

    if (currentGridSession) {
        if (currentGridSession.columns) {
            visibilityModel = Object.keys(currentGridSession.columns).reduce(
                (prev, curr) => {
                    const columnFieldName =
                        columns.find((c) => c.fbKey === curr)?.field ?? curr;

                    return {
                        ...prev,
                        [columnFieldName]:
                            currentGridSession.columns?.[curr] ?? false,
                    };
                },
                {}
            );
        }

        sortingOrder = currentGridSession?.col_order?.map(
            (col: any) => columns.find((c) => c.fbKey === col)?.field ?? col
        );
    }

    return {
        visibilityModel,
        sortingOrder,
    };
};

/**
 * Convert MUI DataGrid API fields to Ops Portal compliant UserSession session fields for sorting and column visibility
 * @param newSession - MUI DataGrid config settings for sorting and column visibility
 * @param columns - columns from the data grid to assist with mapping. Acts as a lookup object
 * @returns data structure compatible with Ops Portal's grid implementation
 */
export const mapLocalSessionToFb = (
    newSession: BackOfficeSession,
    columns: DataTableColumn[]
): OpsPortalColumnSettings => {
    let visibilityModel = undefined;
    let sortingOrder = undefined;

    if (newSession) {
        visibilityModel = newSession?.visibilityModel
            ? Object.keys(newSession.visibilityModel).reduce(
                  (prev, curr) => ({
                      ...prev,
                      [columns.find((c) => c.field === curr)?.fbKey ?? curr]:
                          newSession.visibilityModel[curr],
                  }),
                  {}
              )
            : undefined;

        sortingOrder = newSession?.sortingOrder?.map(
            (col) => columns.find((c) => c.field === col)?.fbKey ?? col
        );
    }

    return {
        columns: visibilityModel,
        col_order: sortingOrder,
    };
};
